import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ExamRoomBlock } from '@/types/settings-api';

/**
 * Canonical React Query key for a practice's exam-room list.
 */
export function examRoomListQueryKey(
  practiceId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'exam-room-list', practiceId];
}

/**
 * Exam rooms for a practice — PROD-1828 (Exam Room in the practitioner
 * Schedule tab).
 *
 *   GET /api/Practice/EditPracticeExamRoom/{practiceId}
 *
 * Response is an ARRAY with a single block (verified against dev cURL
 * 2026-08-06): `[{ practiceId, noOfExamHours, examRooms: [{ examHourId,
 * examRoom }], deletedId: [] }]`. Callers read `[0].examRooms` for the
 * dropdown; each option's value is `examHourId` (the id the appointment
 * profile stores as `roomId`) and its label is `examRoom`.
 *
 * Flutter source: `settings_data_source_impl.dart:1165` (`fetchExamRoomList`).
 * Same endpoint the WH form and the appointment-profile editor both read.
 */
export function useExamRoomList(practiceId: string | null | undefined) {
  return useQuery({
    queryKey: examRoomListQueryKey(practiceId),
    queryFn: ({ signal }) => {
      if (!practiceId) {
        throw new Error('useExamRoomList called without practiceId');
      }
      return get<ExamRoomBlock[]>(
        `/api/Practice/EditPracticeExamRoom/${practiceId}`,
        { signal },
      );
    },
    enabled: !!practiceId,
    staleTime: 60_000,
  });
}
